import { useCallback, useRef, useState } from 'react';
import styles from './ResizeHandle.module.css';

export interface ResizeHandleProps {
  /**
   * Which edge the handle sits on.
   * - 'right': Handle on the right edge of a left-side panel.
   *   Dragging right increases width.
   * - 'left': Handle on the left edge of a right-side panel.
   *   Dragging left increases width.
   */
  side: 'left' | 'right';
  /** Called on each mouse or touch move with the computed new width */
  onResize: (width: number) => void;
  /** Called when the drag starts */
  onResizeStart?: () => void;
  /** Called when the drag ends with the final clamped width in pixels */
  onResizeEnd?: (width: number) => void;
  /** Called on double-click (typically resets to default width) */
  onDoubleClick?: () => void;
  /** Minimum allowed width in pixels */
  minWidth?: number;
  /** Maximum allowed width in pixels */
  maxWidth?: number;
}

/**
 * Draggable resize handle for sidebar panels.
 *
 * Place inside a `position: relative` container. The handle renders as a thin
 * strip on the specified edge, invisible by default with a subtle line on hover
 * and a green (--accent) line during active drag.
 *
 * UX features:
 * - col-resize cursor on hover and during drag
 * - Drag with a mouse, a finger or a pen; a 44px hit area on touch
 * - Min/max width clamping
 * - Double-click to reset to default
 * - Prevents text selection during drag
 */
export function ResizeHandle({
  side,
  onResize,
  onResizeStart,
  onResizeEnd,
  onDoubleClick,
  minWidth = 100,
  maxWidth = 600,
}: ResizeHandleProps) {
  const [active, setActive] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      setActive(true);
      onResizeStart?.();

      // Get the sidebar element (parent of the handle)
      const sidebarEl = containerRef.current?.parentElement;
      if (!sidebarEl) return;

      const rect = sidebarEl.getBoundingClientRect();
      let lastWidth = Math.max(minWidth, Math.min(maxWidth, rect.width));

      // Prevent text selection during drag
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const handleMouseMove = (moveE: MouseEvent) => {
        let newWidth: number;

        if (side === 'right') {
          // Left sidebar: width = mouse X - sidebar left edge
          newWidth = moveE.clientX - rect.left;
        } else {
          // Right sidebar: width = sidebar right edge - mouse X
          newWidth = rect.right - moveE.clientX;
        }

        // Clamp to min/max
        newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
        lastWidth = newWidth;
        onResize(newWidth);
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        setActive(false);
        onResizeEnd?.(lastWidth);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [side, onResize, onResizeStart, onResizeEnd, minWidth, maxWidth]
  );

  // A touch drag sends no mousemove, so touch and pen use pointer events.
  // The handle captures the pointer, and the document follows it until the
  // drag ends, also when the panel closes during the drag. A mouse uses the
  // mouse path above.
  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'mouse') return;
      // Stops the mouse events that the browser makes from a tap.
      e.preventDefault();
      e.stopPropagation();

      const sidebarEl = e.currentTarget.parentElement;
      if (!sidebarEl) return;

      const pointerId = e.pointerId;
      e.currentTarget.setPointerCapture(pointerId);
      setActive(true);
      onResizeStart?.();

      const rect = sidebarEl.getBoundingClientRect();
      let lastWidth = Math.max(minWidth, Math.min(maxWidth, rect.width));
      document.body.style.userSelect = 'none';

      const handlePointerMove = (moveE: PointerEvent) => {
        if (moveE.pointerId !== pointerId) return;
        const newWidth =
          side === 'right' ? moveE.clientX - rect.left : rect.right - moveE.clientX;
        lastWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
        onResize(lastWidth);
      };

      const handlePointerEnd = (endE: PointerEvent) => {
        if (endE.pointerId !== pointerId) return;
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerEnd);
        document.removeEventListener('pointercancel', handlePointerEnd);
        document.body.style.userSelect = '';
        setActive(false);
        onResizeEnd?.(lastWidth);
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerEnd);
      document.addEventListener('pointercancel', handlePointerEnd);
    },
    [side, onResize, onResizeStart, onResizeEnd, minWidth, maxWidth]
  );

  const handleClasses = [
    styles.handle,
    side === 'left' ? styles.handleLeft : styles.handleRight,
    active ? styles.handleActive : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={containerRef}
      className={handleClasses}
      onMouseDown={handleMouseDown}
      onPointerDown={handlePointerDown}
      onDoubleClick={onDoubleClick}
    />
  );
}
